import AssignGroupModal from 'app/pages/team/groups/group/AssignGroupModal';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import ResponsiveDialogActions from 'app/shared-components/layout/dialog/ResponsiveDialogActions';
import type { GetCardsCardGroupPayload } from 'CDK_API';
import { Button, DialogContent, DialogTitle } from '@mui/material';
import { useAppSelector } from 'app/store';
import { DeleteOutline, Lock, LockOpen } from '@mui/icons-material';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { selectAllGroups } from 'app/pages/store/groupsSlice';
import { LoadingButton } from '@mui/lab';
import { NO_GROUP } from 'app/pages/cards/cards/NewCardSheet/newCardUtils';
import { useCardActions } from 'app/hooks/useCardActions';
import { sentryMask } from 'sentryReplayPrivacy';

export enum ModalType {
  lock = 'lock',
  unlock = 'unlock',
  cancel = 'cancel',
  removeFromGroup = 'removeFromGroup',
  moveToAnotherGroup = 'moveToAnotherGroup',
}

interface ModalConfig<T = void> {
  title: string;
  list: ReactNode;
  message: string;
  function: (param: T) => void;
  icon: ReactNode;
  btnText: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ModalTypesConfig = Record<ModalType, ModalConfig<any>>;

export interface ModalActionsCardProps {
  open: boolean;
  type: ModalType;
  cardName: string;
  paymentCardId: string;
  onClose: () => void;
  onSuccess?: () => void;
  currentGroup?: GetCardsCardGroupPayload | undefined | null;
}

function ModalActionsCard({
  open,
  type,
  cardName,
  paymentCardId,
  onClose,
  onSuccess,
  currentGroup,
}: ModalActionsCardProps) {
  const { t } = useTranslation();
  const [btnLoading, setBtnLoading] = useState(false);
  const groups = useAppSelector(selectAllGroups);

  const {
    lockCard: lockCardAction,
    unlockCard: unlockCardAction,
    cancelCard: cancelCardAction,
    moveCardToGroup: moveCardToGroupAction,
  } = useCardActions({
    onSuccess: () => {
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    },
    onLoadingChange: setBtnLoading,
  });

  const lockCard = () => {
    lockCardAction(paymentCardId);
  };

  const unlockCard = () => {
    unlockCardAction(paymentCardId);
  };

  const cancelCard = () => {
    cancelCardAction({
      paymentCardId,
    });
  };

  const moveGroup = async (groupName: string) => {
    const selectedGroup = groups.find((group: any) => group.id === groupName);

    await moveCardToGroupAction({
      paymentCardId,
      cardGroupId: groupName,
      group: selectedGroup ? { id: selectedGroup.id, name: selectedGroup.name || '' } : null,
    });
  };

  const moveToAnotherGroup = (groupName: string) => {
    moveGroup(groupName);
  };

  const removeFromGroup = () => {
    moveGroup(NO_GROUP);
  };

  const modalTypes: ModalTypesConfig = {
    [ModalType.lock]: {
      title: t('cardActionModal.lock.title'),
      list: <li>{t('cardActionModal.lock.description')}</li>,
      message: t('cardActionModal.lock.message', { cardName }),
      function: lockCard,
      icon: <Lock />,
      btnText: t('cardActionModal.lock.btnText'),
    },
    [ModalType.unlock]: {
      title: t('cardActionModal.unlock.title'),
      list: <li>{t('cardActionModal.unlock.description')}</li>,
      message: t('cardActionModal.unlock.message', { cardName }),
      function: unlockCard,
      icon: <LockOpen />,
      btnText: t('cardActionModal.unlock.btnText'),
    },
    [ModalType.cancel]: {
      title: t('cardActionModal.cancel.title'),
      list: (
        <>
          <li>{t('cardActionModal.cancel.description1')}</li>
          <li>{t('cardActionModal.cancel.description2')}</li>
        </>
      ),
      message: t('cardActionModal.cancel.message', { cardName }),
      function: cancelCard,
      icon: <DeleteOutline />,
      btnText: t('cardActionModal.cancel.btnText'),
    },
    [ModalType.removeFromGroup]: {
      title: t('cardActionModal.removeFromGroup.title'),
      list: <></>,
      message: t('cardActionModal.removeFromGroup.message', { cardName }),
      function: removeFromGroup,
      icon: <DeleteOutline />,
      btnText: t('cardActionModal.removeFromGroup.btnText'),
    },
    [ModalType.moveToAnotherGroup]: {
      title: t('cardActionModal.moveToAnotherGroup.title'),
      list: <></>,
      message: t('cardActionModal.moveToAnotherGroup.message'),
      function: moveToAnotherGroup,
      icon: <ArrowForwardIcon />,
      btnText: t('cardActionModal.moveToAnotherGroup.btnText'),
    },
  };

  return (
    <>
      {type !== ModalType.moveToAnotherGroup ? (
        <ResponsiveDialog
          open={open}
          onClose={onClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle
            id="alert-dialog-title"
            color="error.main"
            sx={{
              fontSize: '2.4rem',
              mt: '2rem',
              p: '2rem 5rem 1rem',
            }}
          >
            {modalTypes[type].title}
          </DialogTitle>
          <DialogContent sx={{ p: '0 5rem' }}>
            <ul style={{ fontWeight: 'normal', marginTop: '0px' }}>{modalTypes[type].list}</ul>
            {/* The message interpolates the card name (D1); the Dialog is a portal. */}
            <p {...sentryMask}>{modalTypes[type].message}</p>
          </DialogContent>
          <ResponsiveDialogActions>
            <Button variant="outlined" onClick={onClose}>
              {t('cancel')}
            </Button>
            <LoadingButton
              loading={btnLoading}
              variant="contained"
              onClick={modalTypes[type].function}
              startIcon={modalTypes[type].icon}
              loadingPosition="start"
              color="error"
            >
              {modalTypes[type].btnText}
            </LoadingButton>
          </ResponsiveDialogActions>
        </ResponsiveDialog>
      ) : (
        <AssignGroupModal
          open={open}
          onClose={onClose}
          loading={btnLoading}
          icon={modalTypes[type].icon}
          btnText={modalTypes[type].btnText}
          onClick={modalTypes[type].function}
          currentGroup={currentGroup}
        />
      )}
    </>
  );
}

export default ModalActionsCard;
